import { Element } from '@phrasee/phrasee-typings/typings/futurama/element'
import { useQueryClient } from '@tanstack/react-query'
import { cond, get, has } from 'lodash'

import { useAppSelector } from 'common/hooks/redux'
import useContent from 'common/hooks/useContent'
import {
  isComponentElement,
  isTemplateElement,
} from 'features/futurama/api/interfaces'
import { VariableCombination } from 'features/unifiedFlow/api/api'

import { contentKeys } from '../../api/queryKeys'
import {
  contentVariantMapper,
  downloadCsvFile,
  downloadXlsFile,
  elementDataFormatter,
  optimizationVariantMapper,
} from '../exportHelpers'

export const FILES_TYPES = {
  CSV: 'csv',
  XLS: 'xls',
} as const

const useDownloadFile = () => {
  const { content } = useContent()
  const queryClient = useQueryClient()
  const accountId = useAppSelector((state) => state.authStates.accountId)

  const getFileData = (
    element: Element,
    isCampaign: boolean,
    statusFilter?: string
  ) =>
    cond([
      [
        () => isCampaign,
        () => {
          const campaignData = queryClient.getQueryData([
            contentKeys.campaign(get(element, 'campaign_id', '')),
          ])

          return [
            elementDataFormatter({
              name: element.display_name || element.name,
              element: campaignData,
              variantsPath: 'data.sl',
              variantsMapperHandler: optimizationVariantMapper,
              statusFilter,
            }),
          ]
        },
      ],
      [
        () => isTemplateElement(element),
        () =>
          get(element, 'elements', []).map((item) =>
            elementDataFormatter({
              name: item.display_name,
              element: item,
              variantsPath: 'variants',
              variantsMapperHandler: contentVariantMapper,
              statusFilter,
            })
          ),
      ],
      [
        () => isComponentElement(element),
        () => {
          // Check if element has personalized variants
          const hasPersonalizedVariants = 'personalized_variants' in element

          const combinationsMap: Record<
            string,
            { key: string; value: string }[]
          > = {}

          if (hasPersonalizedVariants) {
            const personalizationId = element.personalization_id
            const combinationsRegion =
              element.personalization_settings?.configuration?.privacy_region

            const combinationsData = personalizationId
              ? queryClient.getQueryData<VariableCombination>(
                  contentKeys.variableCombinations({
                    personalizationId,
                    region: combinationsRegion,
                    accountId,
                  })
                )
              : undefined

            combinationsData?.items.forEach((item) => {
              combinationsMap[item.id] = item.dimensions
            })

            if (statusFilter === 'live') {
              const personalizationRegion = combinationsRegion || 'eu'

              const liveVariantsData = queryClient.getQueryData([
                'personalization-live-variants',
                accountId,
                personalizationId,
                personalizationRegion,
              ])

              if (liveVariantsData && Array.isArray(liveVariantsData)) {
                const mappedVariants = liveVariantsData.map((variant: any) => ({
                  text: variant.variantText,
                  variant_id: variant.variantId,
                  sortOrder: 0,
                  parent_variant_id: null,
                  combinationId: variant.combinationId,
                }))

                return [
                  elementDataFormatter({
                    name: element.display_name || element.name,
                    element: { variants: mappedVariants },
                    variantsPath: 'variants',
                    variantsMapperHandler: contentVariantMapper,
                    combinationsMap,
                  }),
                ]
              }
            }
          }

          return [
            elementDataFormatter({
              name: element.display_name || element.name,
              element: element,
              variantsPath: hasPersonalizedVariants
                ? 'personalized_variants'
                : 'variants',
              variantsMapperHandler: contentVariantMapper,
              statusFilter,
              combinationsMap,
            }),
          ]
        },
      ],
    ])()

  const downloadFile = (type?: 'csv' | 'xls', statusFilter?: string) => {
    content?.elements?.forEach((element: Element) => {
      const isCampaign = has(element, 'campaign_id')
      const data = getFileData(element, isCampaign, statusFilter)

      if (FILES_TYPES.CSV === type) {
        downloadCsvFile(element, data, content)
      }
      if (FILES_TYPES.XLS === type) {
        downloadXlsFile(element, data, content)
      }
    })
  }

  return {
    downloadFile,
  }
}

export default useDownloadFile
